// Servicio para reprocesar finanzas de un Booking individual desde el
// último snapshot guardado en `stays_events`. Se usa desde el botón
// "Recalcular finanzas" en el modal de detalle del calendario y en la
// tabla de arriendos. Detalle de la operación: backend
// `stays.recalc.controller.js`.
//
// El endpoint NO crea ni elimina bookings — sólo actualiza columnas
// financieras de la fila existente. NO dispara cascadas (aseo, bloqueo,
// lavandería). Bloquea bookings inmutables y bookings con edición manual
// financiera salvo `force=true`.
import { apiFetch } from "./apiClient";

/** Cada elemento del diff "antes → después" de una columna del booking. */
export interface RecalcDelta {
  campo: string;
  antes: unknown;
  despues: unknown;
}

export interface RecalcBookingResponse {
  success: boolean;
  message: string;
  deltas: RecalcDelta[];
  ingreso_af_clp_antes: number | null;
  ingreso_af_clp_despues: number | null;
  reserva_total_propietario_antes: number | null;
  reserva_total_propietario_despues: number | null;
  camposActualizados?: string[];
  useCurrentTasa?: boolean;
  force?: boolean;
  /** Cuando el backend devuelve 409 con manualEdit: true. */
  manualEdit?: boolean;
}

/** Resumen por categoría que devuelve el recálculo masivo. */
export interface RecalcBulkSummary {
  total: number;
  recalculados: number;
  sinCambio: number;
  omitidosInmutables: number;
  omitidosEdicionManual: number;
  sinPayload: number;
  errores: number;
}

export interface RecalcBulkResponse {
  success: boolean;
  message: string;
  summary: RecalcBulkSummary;
  deltaIngresoAfTotal: number;
  deltaClienteTotal: number;
  propiedadesAfectadas: number;
  useCurrentTasa?: boolean;
  force?: boolean;
}

export interface RecalcBulkArgs {
  /** Fecha de ingreso desde (YYYY-MM-DD, inclusive). Omitir = sin tope inferior. */
  from?: string;
  /** Fecha de ingreso hasta (YYYY-MM-DD, inclusive). Omitir = sin tope superior. */
  to?: string;
  /** Igual que el por-reserva: usar la tasa de comisión ACTUAL de la propiedad. */
  useCurrentTasaComision?: boolean;
  /** Igual que el por-reserva: pisar bookings con edición manual financiera. */
  force?: boolean;
}

export interface RecalcBookingArgs {
  /**
   * Si `true`, el script usa la tasa de comisión ACTUAL de la propiedad
   * (recalcula comisiones acorde). Si `false` (default), preserva la tasa
   * histórica snapshotada en `booking.tasa_comision_af` para respetar la
   * regla "snapshot inmutable" del flujo financiero.
   */
  useCurrentTasaComision?: boolean;
  /**
   * Si `true`, sobrepasa el bloqueo cuando el booking tiene ediciones
   * manuales financieras (extensiones, otros cargos, salida tardía con
   * monto, override de subitems). Pisa esos valores con la fórmula del
   * mapper. Usar con cuidado.
   */
  force?: boolean;
}

/** Respuesta de regenerar el aseo vinculado a un arriendo. */
export interface RegenerarAseoResponse {
  success: boolean;
  message: string;
  /** true si el aseo NO existía y se creó de nuevo. */
  regenerado: boolean;
  /** true si el aseo ya existía y estaba pagado (se preservó sin cambios). */
  preservadoPagado: boolean;
  aseoId: number | null;
}

export const staysRecalcApi = {
  /**
   * Recalcula las finanzas del booking `bookingId` desde el último
   * snapshot guardado en stays_events.
   *
   * Códigos de respuesta esperados:
   *   - 200 → ok (revisar `deltas`).
   *   - 400 → ID inválido o booking sin `id_reserval` (no vino de Stays).
   *   - 404 → booking no existe / sin payload almacenado.
   *   - 409 → booking inmutable o con edición manual (revisar `manualEdit`).
   *   - 500 → mapper falló al reprocesar.
   */
  recalcBooking: async (
    bookingId: number,
    args: RecalcBookingArgs = {},
  ): Promise<RecalcBookingResponse | null> => {
    const body = {
      use_current_tasa_comision: !!args.useCurrentTasaComision,
      force: !!args.force,
    };
    return apiFetch<RecalcBookingResponse>(`/stays/recalc-booking/${bookingId}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /**
   * Recálculo MASIVO (global): reprocesa en lote los bookings de Stays cuyo
   * ingreso cae en [from, to]. Aplica las mismas guardas que el por-reserva
   * (inmutable / edición manual salvo force / aseo pagado preservado) y
   * devuelve un resumen por categoría.
   *
   * Códigos: 200 ok; 400 fechas inválidas o rango demasiado grande (>1000).
   */
  recalcBookingsBulk: async (
    args: RecalcBulkArgs = {},
  ): Promise<RecalcBulkResponse | null> => {
    const body = {
      from: args.from,
      to: args.to,
      use_current_tasa_comision: !!args.useCurrentTasaComision,
      force: !!args.force,
    };
    return apiFetch<RecalcBulkResponse>(`/stays/recalc-bookings`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /**
   * Regenera SOLO el aseo vinculado al booking `bookingId` (acción acotada).
   * Recrea el aseo AUTO si fue borrado por error (reusa syncCleaningFromBooking,
   * idempotente, preserva un aseo pagado, bloquea mes cerrado). No toca las
   * finanzas del booking.
   *
   * Códigos: 200 ok; 400 id inválido; 404 reserva no existe; 409 mes cerrado.
   */
  regenerarAseo: async (
    bookingId: number,
  ): Promise<RegenerarAseoResponse | null> => {
    return apiFetch<RegenerarAseoResponse>(`/stays/regenerar-aseo/${bookingId}`, {
      method: "POST",
    });
  },
};
